import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AlertCircle, Loader2, Mail, Lock, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

export const LoginPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [localError, setLocalError] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showLocalLogin, setShowLocalLogin] = useState(false);
  const { loginWithMicrosoft, loginWithPassword, isAuthenticated, loading, msalInitialized, isProcessingRedirect, error: authError } = useAuth();
  const navigate = useNavigate();

  const error = localError || authError;
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleMicrosoftLogin = async () => {
    setLocalError('');
    setIsLoading(true);
    try {
      await loginWithMicrosoft();
    } catch (err) {
      setLocalError(err.message);
      setIsLoading(false);
    }
  };

  const handleLocalLogin = async (e) => {
    e.preventDefault();
    setLocalError('');
    
    if (!email || !password) {
      setLocalError('Please enter both email and password');
      return;
    }
    
    setIsLoading(true);
    try {
      await loginWithPassword(email, password);
      toast.success('Login successful');
      navigate('/dashboard');
    } catch (err) {
      setLocalError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (loading || !msalInitialized || isProcessingRedirect) {
    return (
      <div className="min-h-screen bg-secondary flex items-center justify-center">
        <div className="text-center">
          <img 
            src="/skilltrac-logo-white.svg" 
            alt="Skilltrac" 
            className="h-16 w-auto mx-auto mb-6"
          />
          <Loader2 className="w-8 h-8 animate-spin text-lime mx-auto mb-3" />
          <p className="text-white/70 text-sm">
            {isProcessingRedirect ? 'Completing sign in...' : 'Initializing...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* Main Content - Split Layout */}
      <div className="flex-1 flex">
        {/* Left Side - Image */}
        <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 bg-secondary relative overflow-hidden">
          {/* Background Image - Female colleagues working together */}
          <img 
            src="https://images.unsplash.com/photo-1573164713988-8665fc963095?auto=format&fit=crop&w=1920&q=80"
            alt="Female colleagues collaborating in office"
            className="absolute inset-0 w-full h-full object-cover opacity-40"
          />
          
          {/* Overlay Content */}
          <div className="relative z-10 flex flex-col justify-between p-12 w-full">
            {/* Logo */}
            <div>
              <div className="inline-flex bg-white/95 rounded-lg px-3 py-2 mb-4" data-testid="hero-technofocus-logo">
                <img 
                  src="/technofocus-logo.png" 
                  alt="Technofocus" 
                  className="h-5 w-auto"
                />
              </div>
              <img 
                src="/skilltrac-logo-white.svg" 
                alt="Skilltrac" 
                className="h-16 w-auto"
              />
            </div>
            
            {/* Tagline */}
            <div className="max-w-lg">
              <h1 className="text-3xl font-heading font-bold text-white mb-4">
                Training Delivery Management System
              </h1>
              <p className="text-white/70 text-lg leading-relaxed">
                Streamline your partner training programs with powerful tools for scheduling, tracking, and reporting.
              </p>
            </div>
            
            {/* Empty div to maintain spacing */}
            <div></div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="w-full lg:w-1/2 xl:w-2/5 flex flex-col bg-background">
          {/* Mobile Header */}
          <div className="lg:hidden bg-secondary p-4">
            <img 
              src="/skilltrac-logo-white.svg" 
              alt="Skilltrac" 
              className="h-10 w-auto"
            />
          </div>

          {/* Login Form Container */}
          <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
            <div className="w-full max-w-sm">
              {/* Welcome Text with Logo */}
              <div className="text-center mb-8">
                <div className="flex justify-center mb-3" data-testid="form-technofocus-logo">
                  <img 
                    src="/technofocus-logo.png" 
                    alt="Technofocus" 
                    className="h-5 w-auto opacity-80"
                  />
                </div>
                <div className="flex justify-center mb-4">
                  <img 
                    src="/skilltrac-logo-dark.svg" 
                    alt="Skilltrac" 
                    className="h-10 w-auto"
                  />
                </div>
                <h2 className="text-xl font-heading font-semibold text-foreground">
                  Welcome back
                </h2>
                <p className="text-muted-foreground text-sm mt-1">
                  Sign in to your account to continue
                </p>
              </div>

              {/* Error Alert */}
              {error && (
                <Alert variant="destructive" className="mb-4" data-testid="login-error">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="text-sm">{error}</AlertDescription>
                </Alert>
              )}

              {/* Login Options */}
              {!showLocalLogin ? (
                <div className="space-y-4">
                  <Button 
                    onClick={handleMicrosoftLogin}
                    className="w-full h-11 bg-[#2F2F2F] hover:bg-[#1a1a1a] text-white font-medium text-sm"
                    disabled={isLoading}
                    data-testid="microsoft-login-button"
                  >
                    {isLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <svg className="w-5 h-5 mr-2" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
                        <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
                        <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
                        <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
                      </svg>
                    )}
                    Sign in with Microsoft
                  </Button>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-[10px] uppercase">
                      <span className="bg-background px-2 text-muted-foreground">or continue with</span>
                    </div>
                  </div>

                  <Button 
                    variant="outline"
                    onClick={() => setShowLocalLogin(true)}
                    className="w-full h-11 text-sm"
                    data-testid="local-login-toggle"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Email & Password
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <form onSubmit={handleLocalLogin} className="space-y-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="email" className="text-xs font-medium">Email Address</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="email"
                          type="email"
                          placeholder="you@company.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="pl-10 h-11 text-sm"
                          data-testid="email-input"
                          disabled={isLoading}
                        />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="password" className="text-xs font-medium">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="password"
                          type="password"
                          placeholder="Enter your password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="pl-10 h-11 text-sm"
                          data-testid="password-input"
                          disabled={isLoading}
                        />
                      </div>
                    </div>
                    <Button 
                      type="submit"
                      className="w-full h-11 bg-lime hover:bg-lime/90 text-lime-foreground font-medium text-sm"
                      disabled={isLoading}
                      data-testid="local-login-submit"
                    >
                      {isLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                      Sign In
                    </Button>
                  </form>

                  <Button 
                    variant="ghost"
                    onClick={() => {
                      setShowLocalLogin(false);
                      setLocalError('');
                    }}
                    className="w-full text-sm text-muted-foreground"
                  >
                    ← Back to all sign in options
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t bg-muted/30">
            <div className="flex flex-col items-center gap-3 text-center">
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-muted-foreground">Powered & maintained by</span>
                <a 
                  href="https://technofocus.ai" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
                >
                  <img 
                    src="/technofocus-logo.png" 
                    alt="Technofocus" 
                    className="h-4 w-auto opacity-60"
                  />
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
              <p className="text-[10px] text-muted-foreground">
                © {currentYear} Technofocus Pte Ltd. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
